/**
 * Conversation Service
 * Checks conversation status and fetches existing letters for the conversation
 */

export interface ConversationLetter {
  letter_id: string;
  title: string;
  direction: string;
  user_letter_date: string;
  uploaded_by: string;
  tags: string[];
  internet_message_id: string;
  conversation_id: string;
  createdAt: string;
  sk: string;
}

export interface ConversationCheckResponse {
  success: boolean;
  data: {
    letters: ConversationLetter[];
    isNewConversation: boolean;
    isDuplicateMessage: boolean;
    existingLetter?: {
      letter_id: string;
      title: string;
      sk: string;
      createdAt: string;
    };
  };
  metadata: {
    companyName: string;
    baseCollectionName: string;
    conversationId: string;
    letterCount: number;
  };
}

/**
 * Check conversation status and fetch existing letters
 */
export async function checkConversation(
  companyName: string,
  baseCollectionName: string,
  conversationId: string,
  internetMessageId: string
): Promise<ConversationCheckResponse> {
  const normalizedCompanyName = String(companyName || "").trim();
  const normalizedBaseCollectionName = String(baseCollectionName || "").trim();
  const normalizedConversationId = String(conversationId || "").trim();
  const normalizedInternetMessageId = String(internetMessageId || "")
    .trim()
    .replace(/^<+|>+$/g, "")
    .toLowerCase();

  console.log('[CONVERSATION_SERVICE] Checking conversation:', {
    companyName: normalizedCompanyName,
    baseCollectionName: normalizedBaseCollectionName,
    conversationId: normalizedConversationId.substring(0, 20) + '...',
    internetMessageId: normalizedInternetMessageId
  });

  try {
    const response = await fetch('/api/outlook/conversation', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        companyName: normalizedCompanyName,
        baseCollectionName: normalizedBaseCollectionName,
        conversationId: normalizedConversationId,
        internetMessageId: normalizedInternetMessageId
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to check conversation: ${response.statusText}`);
    }

    const data: ConversationCheckResponse = await response.json();

    console.log('[CONVERSATION_SERVICE] Check complete:', {
      isNewConversation: data.data.isNewConversation,
      isDuplicateMessage: data.data.isDuplicateMessage,
      letterCount: data.data.letters.length
    });

    return data;

  } catch (error) {
    console.error('[CONVERSATION_SERVICE] Error checking conversation:', error);
    throw error;
  }
}
